import React from 'react';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import PageMeta from '../components/PageMeta';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Message Sent!",
      description: "Thank you for contacting us. We will get back to you soon.",
    });
    e.target.reset();
  };

  return (
    <div className="contact-page">
      <PageMeta
        title="Contact Us"
        description="Get in touch with BluekraftMed. We're here to help with healthcare staffing 24/7. Contact our team for staffing solutions."
        path="/contact"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">Contact Us</h1>
            <p className="text-xl leading-relaxed">
              Get in touch with our team. We're here to help you 24/7
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <Card className="text-center hover:shadow-xl transition-shadow duration-300">
              <CardHeader>
                <div className="bg-cyan-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-8 h-8 text-cyan-600" />
                </div>
                <CardTitle>Call Us</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-2">Available 24/7</p>
                <a href="tel:+14694662313" className="text-cyan-600 font-semibold text-lg hover:text-cyan-700">
                  +1 (469)-466-2313
                </a>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-shadow duration-300">
              <CardHeader>
                <div className="bg-cyan-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-cyan-600" />
                </div>
                <CardTitle>Email Us</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-2">Quick Response</p>
                <a href="mailto:info@bluekraftmed.com" className="text-cyan-600 font-semibold text-lg hover:text-cyan-700">
                  info@bluekraftmed.com
                </a>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-shadow duration-300">
              <CardHeader>
                <div className="bg-cyan-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-cyan-600" />
                </div>
                <CardTitle>Business Hours</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-2">24/7 Support</p>
                <p className="text-cyan-600 font-semibold text-lg">
                  Always Available
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="max-w-3xl mx-auto">
            <Card className="shadow-xl">
              <CardHeader>
                <div className="text-center">
                  <Mail className="w-16 h-16 text-cyan-600 mx-auto mb-4" />
                  <CardTitle className="text-3xl">Send us a Message</CardTitle>
                  <p className="text-gray-600 mt-2">We will get back as soon as possible</p>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <Input placeholder="Enter your full name" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <Input type="tel" placeholder="Enter your phone number" required />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <Input type="email" placeholder="Enter your email" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subject *
                      </label>
                      <Input placeholder="Enter subject" required />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <Textarea placeholder="Enter your message" rows={6} required />
                  </div>
                  <Button type="submit" className="w-full bg-cyan-600 hover:bg-cyan-700 text-white py-6 text-lg">
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Map or Additional Info */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-8">Visit Our Office</h2>
            <p className="text-gray-600 text-lg mb-8">
              We're here to serve you and answer any questions you may have about our healthcare staffing services.
            </p>
            <div className="bg-gray-100 rounded-lg p-12">
              <MapPin className="w-16 h-16 text-cyan-600 mx-auto mb-4" />
              <p className="text-xl font-semibold mb-2">BluekraftMed Office</p>
              <p className="text-gray-600">United States</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;